import React from 'react';
import { CITIZEN_FORM, CRAFT } from './CitizenPortrait';
import { HatGlyph } from './HatIcon';

/*
 * The day shift.
 *
 * Night crews repair: something fails, a ship holds off in the dark, and a
 * worker is lowered on a line to weld it. Day crews survey, which is the
 * opposite job in every respect -- nothing is wrong. The craft comes down and
 * LANDS on the panel's top rail, the worker steps out onto the surface, walks
 * the span setting marks and running a measure between them, then boards and
 * lifts off. Feet on the plating, in the open, with nothing broken.
 *
 * Fixed phases, so this is keyframes rather than state: a survey takes as long
 * as a survey takes. The repair could not be authored that way because its
 * length depends on how bad the damage was.
 *
 * Strictly decorative: aria-hidden and pointer-transparent.
 */

const VB_W = 240;
const VB_H = 64;
const CRAFT_SCALE = 1.5;

/*
 * The panel's own top edge. Everything stands ON this line, so the container is
 * lifted by exactly this much -- the offset is exported into the inline style
 * rather than written into the stylesheet, because a hand-set value put the
 * rail eight pixels inside the panel and the crew landed in the content.
 *
 * The viewBox is 240 wide and the container is 240px, so one unit is one pixel
 * and the two cannot drift. Changing one without the other would sink them.
 */
export const RAIL = 54;

/*
 * How far a craft hangs below its own centre, read off its geometry. Craft
 * differ: an airship's gondola drops 8.4 units, a balloon's basket 13.4, a
 * glider's wing 3.5. Resting them all at one height left two of the three
 * either floating or buried.
 */
const rideHeight = (craft) =>
  Math.max(
    ...craft.parts.flatMap((part) =>
      part.d
        .match(/-?\d+(?:\.\d+)?/g)
        .map(Number)
        .filter((_, i) => i % 2 === 1),
    ),
  ) * CRAFT_SCALE;

/* Marks set along the span, each planted as the worker reaches it. */
const MARKS = [78, 112, 146, 180];

const SurveyCrew = ({ id }) => {
  const craft = CRAFT[id];
  const form = CITIZEN_FORM[id];

  return (
    <div
      className="survey"
      style={{
        // Lifted so the rail sits on the panel's top border, not below it.
        top: `${-RAIL}px`,
        '--cz-ink': form.ink,
        '--accent': `var(--tmc-hat-${id})`,
      }}
      aria-hidden="true"
    >
      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} focusable="false">
        {/* The measure, run between the first mark and the last. */}
        <line className="survey-measure" x1={MARKS[0]} y1={RAIL - 7} x2={MARKS[MARKS.length - 1]} y2={RAIL - 7} />

        {MARKS.map((x, i) => (
          <g className="survey-mark" key={x} style={{ animationDelay: `${5.5 + i * 1.6}s` }}>
            <line x1={x} y1={RAIL} x2={x} y2={RAIL - 11} />
            <path d={`M ${x} ${RAIL - 11} L ${x + 7} ${RAIL - 9} L ${x} ${RAIL - 7} Z`} />
          </g>
        ))}

        {/* Set down on the rail, resting on its own lowest point. */}
        <g className="survey-craft">
          <g transform={`translate(44 ${RAIL - rideHeight(craft)}) scale(${CRAFT_SCALE})`}>
            {craft.parts.map((part, i) => (
              <path
                key={i}
                className={`${part.line ? 'survey-line' : ''}${part.alt ? ' survey-alt' : ''}`.trim() || undefined}
                d={part.d}
              />
            ))}
            {(craft.glass ?? []).map((pane, i) => (
              <path key={`g${i}`} className="survey-glass" d={pane} />
            ))}
            {(craft.seams ?? []).map((seam, i) => (
              <path key={`s${i}`} className="survey-seam" d={seam} />
            ))}
            {craft.badge && (
              <g className="survey-livery" transform={`translate(${craft.badge.x} ${craft.badge.y})`}>
                <HatGlyph hatId={id} size={craft.badge.size} />
              </g>
            )}
          </g>
        </g>

        {/* Out of the hatch and along the span, on foot. */}
        <g className="survey-worker">
          <g transform={`translate(0 ${RAIL}) scale(0.62)`}>
            {form.parts.map((part, i) => (
              <path key={i} className={part.line ? 'survey-line' : undefined} d={part.d} />
            ))}
            {(form.mark ?? []).map((detail, i) => (
              <path
                key={`m${i}`}
                className={`survey-eye${detail.line ? ' is-line' : ''}`}
                d={detail.d}
                style={{ '--cz-mark-ink': form.markInk ?? `var(--tmc-hat-${id})` }}
              />
            ))}
          </g>
        </g>
      </svg>
    </div>
  );
};

export default SurveyCrew;
